import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StockValueStrip, { useInventoryValueSummary } from './StockValueStrip';
import {
  getSavedWarehouseId,
  saveWarehouseId,
  useProjectWarehouses,
  validWarehouseId
} from '../../hooks/useInventorySelection';

/** One open-lends figure; opens the Lends page on that tab. */
const LendFigure = ({ label, value, tone, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-xl border px-3 py-2 text-left transition-shadow hover:shadow ${
      tone === 'danger' && Number(value) > 0
        ? 'border-red-200 bg-red-50'
        : 'border-gray-200 bg-[#F4F7F8]'
    }`}>
    <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">{label}</span>
    <span
      className={`mt-1 block text-2xl font-bold leading-none tabular-nums ${
        tone === 'danger' && Number(value) > 0 ? 'text-red-700' : 'text-gray-900'
      }`}>
      {value ?? '—'}
    </span>
  </button>
);

/**
 * W4.3: stock value on the inventory dashboard, by warehouse, with open lends
 * and a link to the material cost report.
 */
const InventoryValuePanel = ({ projectId }) => {
  const navigate = useNavigate();
  const { active: activeWarehouses, hasMany } = useProjectWarehouses(projectId);
  const [picked, setPicked] = useState(() => getSavedWarehouseId(projectId));
  const warehouseId = validWarehouseId(picked, activeWarehouses);
  const { data: summary, isError } = useInventoryValueSummary(projectId);
  const openLends = summary?.open_lends;

  useEffect(() => {
    setPicked(getSavedWarehouseId(projectId));
  }, [projectId]);

  // Older servers have no report route; leave the dashboard as it was.
  if (!projectId || isError) return null;

  const openLendsPage = (direction, overdue) =>
    navigate('/builder/inventory/lends', {
      state: { projectID: Number(projectId), direction, ...(overdue && { overdue: true }) }
    });

  return (
    <div className="w-full rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-xl font-semibold text-[#1E4A53]">Stock value</h3>
          <p className="text-sm text-gray-500">At the average rate each item was bought at</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {hasMany && (
            <select
              aria-label="Warehouse"
              value={warehouseId || ''}
              onChange={(e) => {
                const next = e.target.value ? Number(e.target.value) : '';
                setPicked(next);
                saveWarehouseId(projectId, next);
              }}
              className="h-9 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800">
              <option value="">All warehouses</option>
              {activeWarehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                  {w.is_default ? ' (default)' : ''}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={() =>
              navigate('/builder/inventory/material-cost', {
                state: { projectID: Number(projectId) }
              })
            }
            className="h-9 rounded-lg border border-[#2A565E] px-3 text-sm font-semibold text-[#2A565E]">
            Material cost
          </button>
        </div>
      </div>

      <StockValueStrip projectId={projectId} warehouseId={warehouseId} />

      <div className="mt-4">
        <p className="mb-2 text-sm font-semibold text-gray-700">Open lends</p>
        <div className="grid grid-cols-3 gap-3">
          <LendFigure
            label="Lent out"
            value={openLends?.lent_out}
            onClick={() => openLendsPage('lent_out')}
          />
          <LendFigure
            label="Borrowed"
            value={openLends?.borrowed}
            onClick={() => openLendsPage('borrowed')}
          />
          <LendFigure
            label="Overdue"
            tone="danger"
            value={openLends?.overdue}
            onClick={() => openLendsPage('lent_out', true)}
          />
        </div>
      </div>
    </div>
  );
};

export default InventoryValuePanel;
